import {
  Component,
  OnInit,
  ChangeDetectorRef,
  ViewChild, OnDestroy,
  AfterViewChecked,
  AfterViewInit,
} from '@angular/core';
import { Router, Scroll, NavigationEnd } from '@angular/router';

import { MatDialog } from '@angular/material/dialog';
import { MatDrawerContent, MatSidenav } from '@angular/material/sidenav';
import { ExtendedScrollToOptions, ScrollDispatcher, CdkScrollable } from '@angular/cdk/scrolling';

import { filter, tap } from 'rxjs/operators';

import { FloatingNavigationService } from '../../services/floating-navigation/floating-navigation.service';
import { FloatingNavigationModel } from '../../models/floating-navigation.model';
import { fabAnimations } from '../../constants/animations';

@Component({
  selector: 'dls-shell',
  templateUrl: './shell.component.html',
  styleUrls: ['./shell.component.scss'],
  animations: [
    fabAnimations.fab
  ]
})

export class ShellComponent implements OnInit, AfterViewInit, AfterViewChecked, OnDestroy {

  screenWidth: number;
  leftSideNavBreakpoint: number;
  rightSideNavBreakpoint: number;
  showPageNavigation: boolean;
  fabState: 'enter' | 'leave' | null;

  private _lastScrollPosition: number;
  private _fabThreshold: number;

  private _scrollOptions: ExtendedScrollToOptions;

  private _previousScrollHeight: number;
  private _currentScrollHeight: number;

  @ViewChild('sidenavStart', { static: true }) sidenavStart: MatSidenav;

  @ViewChild('sideNavContent', { static: true }) matDrawerContent: MatDrawerContent;

  constructor(public dialog: MatDialog,
    private cd: ChangeDetectorRef,
    private floatingNavSrvc: FloatingNavigationService,
    private router: Router,
    private scrollDispatcher: ScrollDispatcher,
  ) {
    // set screenWidth on page load
    this.screenWidth = window.innerWidth;

    this.leftSideNavBreakpoint = 1024;
    this.rightSideNavBreakpoint = 1440;

    // TODO: Redo with RxJS
    window.onresize = () => {
      // set screenWidth on screen size change
      this.screenWidth = window.innerWidth;
    };

    this.showPageNavigation = false;

    // User need to scroll maximum header height
    // to see the FAB
    this._fabThreshold = 130;
    this.fabState = null;
    this._lastScrollPosition = 0;
  }

  // Navigates the page to the top
  public backToTop() {
    this._scrollOptions = {
      top: 0,
      behavior: 'smooth'
    };
    // Scroll to position
    this.matDrawerContent.scrollTo(this._scrollOptions);
  }

  ngOnInit(): void {

    // -------------- Right Side Navigation: Start ------------------
    this.floatingNavSrvc.getScrollHeight().subscribe((scrollPosition) => {
      this._scrollOptions = {
        top: scrollPosition,
        behavior: 'smooth'
      };
      // Scroll to position
      this.matDrawerContent.scrollTo(this._scrollOptions);
    });

    this.floatingNavSrvc
      .getFloatingNavItems()
      .subscribe((navItems: FloatingNavigationModel[]) => {
        this.showPageNavigation = navItems.length > 0;
      });
    // -------------- Right Side Navigation: End ------------------


    // -------------- Router: Scroll
    // Scroll Event after Navigation End
    this.router.events.pipe(
      tap((event) => {
        if (event instanceof NavigationEnd) {

          // Clear navigation items
          this.floatingNavSrvc.clearNavItems();

          // Forcing navigation height recalculation
          this.floatingNavSrvc.viewHeightChanged(true);

          // Close the left side nav drawer after navigation ends
          // For resolution lower than leftSideNavBreakpoint
          if (this.screenWidth < this.leftSideNavBreakpoint && this.sidenavStart.opened) {
            this.sidenavStart.close();
          }
        }

      }),
      filter((e) => e instanceof Scroll)
    ).subscribe((e: Scroll) => {
      if (!e.anchor) {

        // Properties for Scroll
        this._scrollOptions = {
          top: 0,
          behavior: 'auto'
        };

        // If no fragment available then open page from the top
        this.matDrawerContent.scrollTo(this._scrollOptions);
      }
    });

    // -------------- Floating Action Button-----------
    this.scrollDispatcher.scrolled().subscribe((data: CdkScrollable) => {
      const scrollPosition = data.getElementRef().nativeElement.scrollTop;

      // Change FAB state only when its scroll down towards FAB threshold
      if (
        scrollPosition > this._fabThreshold
        && scrollPosition > this._lastScrollPosition
        && this.fabState !== 'enter'
      ) {
        this.fabState = 'enter';
        this.cd.detectChanges();
      }

      // Change FAB state only when its scroll up towards FAB threshold
      if (
        scrollPosition < this._fabThreshold
        && scrollPosition < this._lastScrollPosition
        && this.fabState !== 'leave'
      ) {
        this.fabState = 'leave';
        this.cd.detectChanges();
      }
      this._lastScrollPosition = scrollPosition;
    });

  }

  ngAfterViewInit(): void {
    // Forcing navigation height recalculation
    this.floatingNavSrvc.viewHeightChanged(true);

    this.cd.detectChanges();
  }


  ngAfterViewChecked(): void {
    // Preserve previous scroll height
    this._previousScrollHeight = this._currentScrollHeight;

    // Update current scroll height
    this._currentScrollHeight = this.matDrawerContent.getElementRef().nativeElement.scrollHeight;

    // Emit view change event after scroll height detected
    if (this._previousScrollHeight !== this._currentScrollHeight) {
      // Recalculates scroll hooks offsetHeight
      this.floatingNavSrvc.viewHeightChanged();
    }
  }

  ngOnDestroy(): void {
    this.floatingNavSrvc.clearNavItems();
  }

}
